import React, { useState, useEffect } from 'react';
import { adminService } from '../../../services/adminService';
import Icon from '../../../components/AppIcon';

const EdgeFunctionLogsPanel = ({ compact = false, limit = 50 }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    functionName: '',
    logLevel: ''
  });

  useEffect(() => {
    loadLogs();
  }, [filters]);

  const loadLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminService?.getEdgeFunctionLogs({
        limit,
        functionName: filters?.functionName || null,
        logLevel: filters?.logLevel || null
      });
      setLogs(data);
    } catch (err) {
      console.error('Load edge function logs error:', err);
      setError(err?.message || 'Failed to load edge function logs');
    } finally {
      setLoading(false);
    }
  };

  const getLogLevelConfig = (level) => {
    const configs = {
      info: { label: 'Info', color: 'text-primary', bgColor: 'bg-primary/10', icon: 'Info' },
      warn: { label: 'Warning', color: 'text-warning', bgColor: 'bg-warning/10', icon: 'AlertTriangle' },
      error: { label: 'Error', color: 'text-destructive', bgColor: 'bg-destructive/10', icon: 'XCircle' },
      debug: { label: 'Debug', color: 'text-muted-foreground', bgColor: 'bg-muted', icon: 'Bug' }
    };
    return configs?.[level] || configs?.info;
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3"></div>
          <div className="space-y-3">
            <div className="h-16 bg-muted rounded"></div>
            <div className="h-16 bg-muted rounded"></div>
            <div className="h-16 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
            <Icon name="FileText" size={20} className="text-success" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Edge Function Logs</h2>
            <p className="text-sm text-muted-foreground">Serverless function execution monitoring</p>
          </div>
        </div>
        {!compact && (
          <button
            onClick={loadLogs}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="RefreshCw" size={16} className="text-muted-foreground" />
          </button>
        )}
      </div>

      {!compact && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Function Name</label>
            <select
              value={filters?.functionName}
              onChange={(e) => setFilters({ ...filters, functionName: e?.target?.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Functions</option>
              <option value="create-subscription-checkout">create-subscription-checkout</option>
              <option value="stripe-webhook">stripe-webhook</option>
              <option value="send-offer-report">send-offer-report</option>
              <option value="send-welcome-email">send-welcome-email</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Log Level</label>
            <select
              value={filters?.logLevel}
              onChange={(e) => setFilters({ ...filters, logLevel: e?.target?.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Levels</option>
              <option value="info">Info</option>
              <option value="warn">Warning</option>
              <option value="error">Error</option>
              <option value="debug">Debug</option>
            </select>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-4 flex items-start gap-3">
          <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
          <p className="text-destructive text-sm">{error}</p>
        </div>
      )}

      <div className="space-y-3">
        {logs?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Inbox" size={48} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No logs found</p>
          </div>
        ) : (
          logs?.map((log) => {
            const levelConfig = getLogLevelConfig(log?.logLevel);
            return (
              <div key={log?.id} className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium text-foreground">{log?.functionName}</span>
                      <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${levelConfig?.bgColor}`}>
                        <Icon name={levelConfig?.icon} size={12} className={levelConfig?.color} />
                        <span className={`text-xs font-medium ${levelConfig?.color}`}>{levelConfig?.label}</span>
                      </div>
                    </div>
                    <p className="text-sm text-foreground mb-2">{log?.message}</p>
                    {log?.metadata && Object?.keys(log?.metadata)?.length > 0 && (
                      <div className="text-xs text-muted-foreground bg-muted/50 rounded p-2 mb-2 font-mono">
                        {JSON?.stringify(log?.metadata, null, 2)}
                      </div>
                    )}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>{adminService?.formatRelativeTime(log?.createdAt)}</span>
                      <span>{adminService?.formatDate(log?.createdAt)}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default EdgeFunctionLogsPanel;